import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import translate from '../../libs/i18n';
import Header from '../../components/Header/Header';
import InputField from '../../components/InputField/InputField';
import Button from '../../components/Button/Button';
import DefaultAvatar from '../../assets/svgs/DefaultAvatar';
import Api from '../../libs/Api';
import { cpfMask } from '../../utils/cpfMask';
import { telMask } from '../../utils/telMask';
import onlyNumbers from '../../utils/onlyNumbers';
import { registerMeUser } from '../../store/actions/meUser';

import { UserProfileStyle } from './UserProfileStyle';

const UserProfile = () => {
    const dispatch = useDispatch();

    const [name, setName] = useState('');
    const [cpf, setCpf] = useState('');
    const [phone, setPhone] = useState('');
    const [type, setType] = useState('');
    const [inscription, setInscription] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [nameError, setNameError] = useState('');
    const [cpfError, setCpfError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [userInstitutions, setUserInstitutions] = useState([]);

    const language = useSelector((state) => state.settings.language);
    const user = useSelector((state) => state.meUser);

    useEffect(() => {
        setName(user?.name || '');
        setCpf(user?.cpf ? cpfMask(user.cpf) : '');
        setPhone(user?.phone ? telMask(user.phone) : '');
        setInscription(user?.inscription || '');

        if (user?.type === 'RES') {
            setType('Responsável');
        }
        else if (user?.type === 'DOC') {
            setType('Médico');
        }
        else if (user?.type === 'TEC') {
            setType('Técnico');
        }
        else if (user?.type === 'ADM') {
            setType('Administrador');
        }
        else {
            setType('');
        }
        setImageFile(null);
        setImagePreview('');
        setUserInstitutions([]);
    }, [user]);

    useEffect(() => {
      return () => {
        if (imagePreview) {
          URL.revokeObjectURL(imagePreview);
        }
      };
    }, [imagePreview]);

    useEffect(() => {
      const fetchInstitutions = async () => {
        try {
          const response = await Api.getInstitutionsAndPermissions();
          if (response?.data) {
            if (user?.type === 'RES') {
              setUserInstitutions(response.data?.avaliable_to_res?.institutions || []);
            } else if (user?.type && ['DOC', 'TEC'].includes(user.type)) {
              setUserInstitutions(response.data?.avaliable_to_doc_and_tec?.institutions || []);
            } else if (user?.type === 'ADM') {
              const resInstitutions = response.data?.avaliable_to_res?.institutions || [];
              const docTecInstitutions = response.data?.avaliable_to_doc_and_tec?.institutions || [];
              setUserInstitutions([...resInstitutions, ...docTecInstitutions]);
            }
          }
        } catch (error) {
          console.error('Erro ao carregar instituições:', error);
        }
      };

      if (user?.id) {
        fetchInstitutions();
      }
    }, [user?.type, user?.id]);

    const institutionName = useMemo(() => {
      if (Array.isArray(userInstitutions) && userInstitutions.length) {
        console.log(userInstitutions);
        const institutionNames = userInstitutions
          .map((institution) => institution?.name)
          .filter(Boolean);
        return institutionNames.join(', ') || '-';
      }

      return '-';
    }, [userInstitutions]);

    const handleCpfChange = (value) => {
      const numbersOnly = onlyNumbers(value).slice(0, 11);
      setCpf(cpfMask(numbersOnly));
      setCpfError('');
    };

    const handlePhoneChange = (value) => {
      const numbersOnly = onlyNumbers(value).slice(0, 11);
      setPhone(telMask(numbersOnly));
      setPhoneError('');
    };

    const handleImageChange = (event) => {
      const file = event?.target?.files?.[0];

      if (!file) {
        return;
      }

      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }

      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    };

    const validateForm = () => {
      let hasError = false;

      if (!name?.trim()) {
        setNameError(translate('thisFieldIsRequired', language));
        hasError = true;
      } else {
        setNameError('');
      }

      const cpfNumbers = onlyNumbers(cpf);
      if (cpfNumbers.length > 0 && cpfNumbers.length !== 11) {
        setCpfError(translate('cpfMustBeValid', language));
        hasError = true;
      } else {
        setCpfError('');
      }

      return !hasError;
    };

    const handleSaveProfile = async () => {
      setSuccessMessage('');
      setErrorMessage('');

      if (!validateForm()) {
        return;
      }

      setIsSaving(true);

      const payload = new FormData();
      payload.append('name', name.trim());
      payload.append('cpf', onlyNumbers(cpf) || '');
      payload.append('phone', onlyNumbers(phone) || '');
      payload.append('inscription', inscription || '');

      if (imageFile) {
        payload.append('image', imageFile);
      }

      try {
        const response = await Api.patchMeUser(payload);

        if (response?.status >= 200 && response?.status < 300 && response?.data) {
          dispatch(registerMeUser(response.data));
          setSuccessMessage('Perfil atualizado com sucesso.');
          return;
        }

        const apiErrors = response?.data;
        if (apiErrors?.cpf?.[0]) {
          setCpfError(apiErrors.cpf[0]);
        }

        if (apiErrors?.phone?.[0]) {
          setPhoneError(apiErrors.phone[0]);
        }

        setErrorMessage('Não foi possível atualizar o perfil.');
      } catch (error) {
        setErrorMessage('Não foi possível atualizar o perfil.');
      } finally {
        setIsSaving(false);
      }
    };

  return (
    <UserProfileStyle>
      <Header />
      <div className="profile-shell">
        <div className="left-column">
          <div className="profile-avatar">
            {imagePreview || user?.image ? (
              <img src={imagePreview || user.image} alt={user?.name || 'Usuário'} />
            ) : (
              <DefaultAvatar />
            )}
          </div>

          <label className="upload-button" htmlFor="profile-image-input">
            Adicionar foto
          </label>
          <input
            id="profile-image-input"
            type="file"
            accept="image/*"
            className="file-input"
            onChange={handleImageChange}
          />

          <h1 className="profile-name">{user?.name || '-'}</h1>
          <p className="profile-type">{type|| '-'}</p>
        </div>

        <div className="right-column">
          <h2 className="form-title">Meu perfil</h2>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSaveProfile();
            }}
          >
            <div className="field-wrapper">
              <label className="input-label">Email</label>
              <InputField
                type="text"
                inputName="email"
                value={user?.email || ''}
                placeholder="Email"
                disabled
                noMarginBottom
              />
            </div>

            <div className="field-wrapper">
              <label className="input-label">{translate('name', language)}</label>
              <InputField
                type="text"
                inputName="name"
                value={name}
                placeholder="Nome"
                error={nameError}
                onChange={setName}
                noMarginBottom
              />
            </div>

            <div className="inline-fields">
              <div className="field-wrapper">
                <label className="input-label">CPF</label>
                <InputField
                  type="text"
                  inputName="cpf"
                  value={cpf}
                  placeholder="000.000.000-00"
                  error={cpfError}
                  onChange={handleCpfChange}
                  noMarginBottom
                />
              </div>

              <div className="field-wrapper">
                <label className="input-label">{translate('telephone', language)}</label>
                <InputField
                  type="text"
                  inputName="phone"
                  value={phone}
                  placeholder="(00) 00000-0000"
                  error={phoneError}
                  onChange={handlePhoneChange}
                  noMarginBottom
                />
              </div>
            </div>

            <div className="inline-fields">
              <div className="field-wrapper">
                <label className="input-label">{translate('inscription', language) || 'Inscrição'}</label>
                <InputField
                  type="text"
                  inputName="inscription"
                  value={inscription}
                  placeholder="Inscrição"
                  onChange={setInscription}
                  noMarginBottom
                  disabled
                />
              </div>

              <div className="field-wrapper">
                <label className="input-label">{translate('Institution', language)}</label>
                <InputField
                  type="text"
                  inputName="institution"
                  value={institutionName || '-'}
                  placeholder="Instituição"
                  disabled
                  noMarginBottom
                />
              </div>
            </div>

            {successMessage ? <p className="feedback-success">{successMessage}</p> : null}
            {errorMessage ? <p className="feedback-error">{errorMessage}</p> : null}

            <div className="button-wrapper">
              <Button
                type="button"
                text={isSaving ? 'Salvando...' : 'Salvar alterações'}
                theme={isSaving ? 'disabled' : 'default'}
                onClick={handleSaveProfile}
                disabled={isSaving}
              />
            </div>
          </form>
        </div>
      </div>
    </UserProfileStyle>
  );
};

export default UserProfile;